import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';
import { getCurrentUserId } from '@/utils/authHelpers';
import { invokeWithRetry } from './common/invokeWithRetry';
import { isTransportFailure } from './common/isTransportFailure';

/**
 * Phase 8D: User Roles & Auth Wrapper
 * Centralizes user role checking and super admin verification through edge functions
 *
 * Durcissement (audit 0.7) : les deux fonctions basculaient sur un repli
 * `…Legacy()` (lecture directe de `user_roles`, RPC `is_super_admin`) dès que
 * l'appel edge échouait, quelle qu'en soit la raison. Pour des fonctions qui
 * DÉTERMINENT les droits de l'utilisateur, c'est le pire endroit possible pour
 * un contournement : un refus du serveur était réinterprété par une seconde
 * source d'autorité, côté navigateur.
 *
 * Le repli n'est donc plus déclenché que sur une panne de transport
 * (`isTransportFailure`). Sur un refus, on retient la valeur la moins
 * privilégiée possible (`[]` / `false`) plutôt qu'un second avis : c'est le
 * comportement fail-closed attendu d'une brique d'autorisation.
 *
 * `user-management` renvoie bien 401 pour une session invalide et 403 pour un
 * refus de droits (le mappage a été corrigé dans
 * `supabase/functions/user-management/index.ts`) : `authExpired` et `forbidden`
 * sont donc fiables ici.
 */

export type AppRole = 'admin' | 'coach' | 'client' | 'super_admin';

interface GetUserRolesResult {
  roles: AppRole[];
}

interface CheckSuperAdminResult {
  isSuperAdmin: boolean;
}

// Feature flags for gradual rollout
const FEATURE_FLAGS = {
  GET_USER_ROLES: true,
  CHECK_SUPER_ADMIN: true,
};

export function enableAllUserRolesFlags() {
  FEATURE_FLAGS.GET_USER_ROLES = true;
  FEATURE_FLAGS.CHECK_SUPER_ADMIN = true;
}

export function disableAllUserRolesFlags() {
  FEATURE_FLAGS.GET_USER_ROLES = false;
  FEATURE_FLAGS.CHECK_SUPER_ADMIN = false;
}

/**
 * Get all roles for a user in a specific gym
 *
 * Lève désormais si le serveur a refusé la demande (403 rôle/salle, 4xx métier).
 * Aucun appelant dans `src/` à ce jour : le risque de régression est nul, et un
 * futur appelant recevra une erreur explicite au lieu d'une liste de rôles
 * fabriquée par un chemin de contournement.
 */
export async function getUserRoles(userId?: string, gymId?: string): Promise<AppRole[]> {
  if (!FEATURE_FLAGS.GET_USER_ROLES) {
    return getUserRolesLegacy(userId, gymId);
  }

  const targetUserId = userId || await getCurrentUserId();
  const targetGymId = gymId || getUserGymId();

  if (!targetUserId || !targetGymId) {
    console.error('[userRolesWrapper] Missing userId or gymId');
    return [];
  }

  const { data, error, authExpired, forbidden } = await invokeWithRetry('user-management', {
    action: 'get_user_roles',
    gym_id: targetGymId,
  });

  if (authExpired) {
    // Déconnexion et redirection déjà effectuées par `invokeWithRetry`.
    return [];
  }

  if (error) {
    // Refus du serveur : surtout pas de relecture directe de `user_roles`, qui
    // reviendrait à laisser le navigateur trancher une question d'autorisation
    // que le serveur vient de refuser de trancher en sa faveur.
    if (!isTransportFailure(error, { authExpired, forbidden })) {
      console.error('[userRolesWrapper] getUserRoles refused by server, no legacy fallback:', error);
      throw error;
    }
    console.error('[userRolesWrapper] Edge function unreachable, falling back to legacy query:', error);
    return getUserRolesLegacy(userId, gymId);
  }

  return (data as GetUserRolesResult)?.roles ?? [];
}

/**
 * Check if a user is a super admin
 *
 * Exception assumée à la règle « l'erreur remonte » : cette fonction ne lève
 * pas. `useSupabaseAuth.handleAppResumed` l'appelle dans un `Promise.all` sans
 * `try/catch` (listener `capacitor-app-resumed`) ; y lever produirait une
 * `unhandled promise rejection` sans aucune trace pour l'utilisateur. Comme il
 * s'agit d'un prédicat d'autorisation, la valeur de repli sûre est `false`
 * (fail-closed) : on la renvoie sur refus, SANS consulter le repli legacy.
 *
 * À noter : `check_super_admin` répond 200 avec `{ isSuperAdmin: false }` pour
 * un utilisateur ordinaire et court-circuite toute validation de salle. Un 401 /
 * 403 sur cette action signale donc un vrai problème de session ou de droits,
 * jamais un simple « cet utilisateur n'est pas super admin ».
 */
export async function checkSuperAdmin(userId?: string): Promise<boolean> {
  if (!FEATURE_FLAGS.CHECK_SUPER_ADMIN) {
    return checkSuperAdminLegacy(userId);
  }

  try {
    const targetUserId = userId || await getCurrentUserId();

    if (!targetUserId) {
      console.error('[userRolesWrapper] Missing userId');
      return false;
    }

    const { data, error, authExpired, forbidden } = await invokeWithRetry('user-management', {
      action: 'check_super_admin',
      gym_id: '00000000-0000-0000-0000-000000000000', // Super admin doesn't need specific gym
    });

    if (authExpired) {
      return false;
    }

    if (error) {
      // Refus du serveur : pas de RPC `is_super_admin` en second avis, on reste
      // sur la réponse la moins privilégiée.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[userRolesWrapper] checkSuperAdmin refused by server, no legacy fallback:', error);
        return false;
      }
      console.error('[userRolesWrapper] Edge function unreachable, falling back to legacy RPC:', error);
      return checkSuperAdminLegacy(userId);
    }

    return (data as CheckSuperAdminResult)?.isSuperAdmin ?? false;
  } catch (error) {
    // Ne reste ici que l'imprévu local (`getCurrentUserId`) : `invokeWithRetry`
    // ne lève jamais. On garde le repli legacy, il n'y a eu aucune décision
    // serveur à contourner.
    console.error('[userRolesWrapper] Unexpected error:', error);
    return checkSuperAdminLegacy(userId);
  }
}

// ============= Legacy Fallback Functions =============

async function getUserRolesLegacy(userId?: string, gymId?: string): Promise<AppRole[]> {
  try {
    const targetUserId = userId || await getCurrentUserId();
    const targetGymId = gymId || getUserGymId();

    if (!targetUserId || !targetGymId) {
      return [];
    }

    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', targetUserId)
      .eq('gym_id', targetGymId);

    if (error) throw error;

    return (data || []).map(r => r.role as AppRole);
  } catch (error) {
    console.error('[userRolesWrapper] Legacy getUserRoles error:', error);
    return [];
  }
}

async function checkSuperAdminLegacy(userId?: string): Promise<boolean> {
  try {
    const targetUserId = userId || await getCurrentUserId();

    if (!targetUserId) {
      return false;
    }

    const { data, error } = await supabase.rpc('is_super_admin', {
      user_id: targetUserId
    });

    if (error) throw error;

    return data || false;
  } catch (error) {
    console.error('[userRolesWrapper] Legacy checkSuperAdmin error:', error);
    return false;
  }
}
